import { Button, Chip, IconButton, Link, ListItem, ListItemSecondaryAction, ListItemText, ListSubheader, Typography } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Collections } from "Collections";
import { useAppUser } from "components/AppUserProvider";
import { FullscreenLoading } from "components/FullscreenLoading";
import { useOrg } from "components/OrgProvider";
import { useOrgUser } from "components/OrgUserProvider";
import { usePriceFiels } from "components/PriceFieldProvider";
import { usePriceFieldSuggestions } from "components/PriceFieldSuggestionsProvider";
import { FirebaseUtils } from "firebaseUtils/FirebaseUtils";
import { AnalyticsEvent } from "models/AnalyticsEvent";
import { Grant } from "models/Grant";
import { IndustryTypes } from "models/IndustryTypes";
import { getOrgUserGrants } from "models/OrgUser";
import { noPriceFieldNameText, PriceField } from "models/PriceField";
import { PriceFieldCategories } from "models/PriceFieldCategories";
import { PriceFieldTemplate } from "models/PriceFieldTemplate";
import { useSnackbar } from "notistack";
import { Paths } from "Paths";
import React, { useMemo, useState } from "react";
import { formatMoney } from "screens/Offer/CreateOffer/SummaryTable";
import { WarningModal } from "screens/Users/OrgUserScreen";
import { ListScreen } from "../ListScreen";



const getName = (template: PriceFieldTemplate) => noPriceFieldNameText(template.template.priceField.name?.text)

export function ImportPriceFieldsScreen() {
    const { enqueueSnackbar } = useSnackbar()
    const [org, loadingOrg] = useOrg()
    const [appUser, loadingAppUser] = useAppUser()
    const [orgUser, loadingOrgUser] = useOrgUser()
    const [priceFields, loadingPriceFields] = usePriceFiels()
    const [priceFieldSuggestions, loadingPriceFieldSuggestions] = usePriceFieldSuggestions()
    const [showImportWarningModal, setShowImportWarningModal] = useState(false)

    const loading = loadingOrg || loadingOrgUser || loadingPriceFields || loadingAppUser || loadingPriceFieldSuggestions

    const grants = getOrgUserGrants(orgUser?.data || null)
    const canCreate = !!grants?.[Grant.CREATE_PRICE_FIELD]

    const industryType = org && IndustryTypes.FlatList.filter(it => !!org.data.industryTypes.map[it.id])[0]

    const suggestedForUseTemplates = useMemo(() => priceFieldSuggestions && Object.values(priceFieldSuggestions).map(priceField => priceField.data.template?.id), [priceFieldSuggestions])
    const usedTemplates = useMemo(() => priceFields && Object.values(priceFields).map(priceField => priceField.template?.id), [priceFields])

    if (loading) {
        return <FullscreenLoading screenId="Price Fields Screen" />
    }
    if (!org) {
        return (
            <div style={{ display: 'grid', placeItems: 'center', height: '60vh' }}>
                <Typography>Venligst <Link href={Paths.SELECT_ORG}>velg en organisasjon</Link> som dokumentet skal forbindes med</Typography>
            </div>
        )
    }

    return (
        <ListScreen<PriceFieldTemplate>
            standardBackButton
            title="Importer Prisfelter"
            collectionName={Collections.PRICE_FIELD_TEMPLATES}
            // TODO: fix query selector for industry types
            //@ts-ignore
            getQuery={(query, path) => org && industryType && query.where(path.data.template.priceField.industryTypes.map[industryType.id], '==', true)}
            loadingQueryData={loadingOrg}
            persist
            getName={getName}
            noEntriesText="Det er ingen maler i systemet enda"
            fieldName="priceFieldTemplate"
            path={Paths.PRICE_FIELD_TEMPLATES}
            dense
            getActions={(entry) => {
                if (usedTemplates?.includes(entry.data.id)) {
                    return (
                        <Chip
                            label="Lagt til"
                            color="primary"
                        />
                    )
                }
                if (suggestedForUseTemplates?.includes(entry.data.id)) {
                    return (
                        <Chip
                            label="Forslag sendt"
                            color="secondary"
                        />
                    )
                }
                return orgUser ? (
                    <IconButton onClick={() => {
                        FirebaseUtils.createDocument<PriceField>(
                            true,
                            canCreate ? Collections.PRICE_FIELDS : Collections.PRICE_FIELD_SUGGESTIONS,
                            null,
                            appUser?.data || null,
                            orgUser?.data || null,
                            canCreate ? AnalyticsEvent.CREATED_PRICE_FIELD : AnalyticsEvent.CREATED_PRICE_FIELD_SUGGESTION,
                            canCreate ? AnalyticsEvent.IMPORTED_PRICE_FIELD_TEMPLATE : AnalyticsEvent.CREATED_IMPORT_PRICE_FIELD_TEMPLATE_SUGGESTION,
                            canCreate ? AnalyticsEvent.CREATE_PRICE_FIELD_ERROR : AnalyticsEvent.CREATE_PRICE_FIELD_SUGGESTION_ERROR,
                            enqueueSnackbar,
                            canCreate ? 'Prisfelt opprettet' : 'Forslag sendt',
                            {
                                ...entry.data.template.priceField,
                                org: {
                                    id: org.data.id,
                                },
                                template: {
                                    id: entry.data.id,
                                },
                            },
                            (doc) => (canCreate ? { priceFieldId: doc.id, templateId: entry.data.id } : { sugegstionId: doc.id, templateId: entry.data.id }),
                        )
                    }}>
                        <Add />
                    </IconButton>
                ) : null
            }}
            getSecondaryText={({ template: { priceField } }) => (!Object.values(priceField.categories.map).length ? (
                <Typography variant="subtitle2" color="secondary">Har ingen kategorier</Typography>
            ) : formatMoney(priceField.price) + '  ' + PriceFieldCategories.FlatList.filter(category => !!priceField.categories.map[category.id]).map(category => category.name).join(', '))}
            getErrorEvent={AnalyticsEvent.GET_PRICE_FIELDS_ERROR}
            topItems={(entries) => (
                <>
                    {canCreate && entries && showImportWarningModal && (
                        <WarningModal title="Er du sikker?" onCancel={() => setShowImportWarningModal(false)} onClose={() => {
                            setShowImportWarningModal(false)
                            entries.forEach(entry => {
                                if (!usedTemplates?.includes(entry.data.id)) {
                                    FirebaseUtils.createDocument<PriceField>(
                                        true,
                                        Collections.PRICE_FIELDS,
                                        null,
                                        appUser?.data || null,
                                        orgUser?.data || null,
                                        AnalyticsEvent.CREATED_PRICE_FIELD,
                                        AnalyticsEvent.IMPORTED_PRICE_FIELD_TEMPLATE,
                                        AnalyticsEvent.CREATE_PRICE_FIELD_ERROR,
                                        enqueueSnackbar,
                                        null,
                                        {
                                            ...entry.data.template.priceField,
                                            org: {
                                                id: org.data.id,
                                            },
                                            template: {
                                                id: entry.data.id,
                                            },
                                        },
                                        (doc) => ({ priceFieldId: doc.id, templateId: entry.data.id }),
                                    )
                                }
                            })
                        }}>
                            Ønsker du å importere {entries.filter(entry => !usedTemplates?.includes(entry.data.id)).length} av {entries.length} prisfelter som ikke er i bruk enda?
                        </WarningModal>
                    )}
                    {canCreate && (
                        <ListItem>
                            <ListItemText primary="Handling" />
                            <ListItemSecondaryAction>
                                <Button color="secondary" onClick={() => setShowImportWarningModal(true)} >
                                    Importer Alle
                                </Button>
                            </ListItemSecondaryAction>
                        </ListItem>
                    )}
                    <ListSubheader>Bransje: {IndustryTypes.FlatList.filter(type => !!org?.data.industryTypes.map[type.id]).map(type => type.name).join(', ')}</ListSubheader>
                </>
            )}
        />
    )
}
